import React from "react";
import styles from "./KanbanColumn.module.scss";
import { ReactComponent as WarningIcon } from "../../icons/warning.svg";
import { ReactComponent as LinkIcon } from "../../icons/link.svg";
import { ReactComponent as CalendarIcon } from "../../icons/calendar.svg";
import { ReactComponent as UserIcon } from "../../icons/user.svg";
import SmallAuditIcon from "../../icons/small-audit.svg";
import SmallFindingIcon from "../../icons/small-finding.svg";
import SmallImprovementIcon from "../../icons/small-improvement.svg";
import LargeAuditIcon from "../../icons/large-audit.svg";
import LargeFindingIcon from "../../icons/large-finding.svg";
import LargeImprovementIcon from "../../icons/large-improvement.svg";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { pushModal } from "../../redux/modalNavigationStore";
import { hideModal } from "../../redux/modalStore";
import classNames from "classnames";
import { formatShortDate } from "../../utils/helpers";
import { useTranslation } from "react-i18next";

const KanbanColumn = ({ status, items, userEmail, activeFilters }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orgId } = useParams();

  const meModeActive = useSelector((state) => state.filters.meModeActive);

  const visibleItems = [...items].slice(0, 4);

  const getType = (item) => {
    if (item.key?.startsWith("A-")) return "A";
    if (item.key?.startsWith("F-")) return "F";
    if (item.key?.startsWith("I-")) return "I";
    return "?";
  };

  const typeMap = {
    A: `/${orgId}/audits`,
    F: `/${orgId}/findings`,
    I: `/${orgId}/improvement-projects`,
  };

  const iconAltKeyMap = {
    A: "kanban.icons.audit",
    F: "kanban.icons.finding",
    I: "kanban.icons.improvement",
  };

  const typeCounts = {
    A: items.filter((item) => getType(item) === "A").length,
    F: items.filter((item) => getType(item) === "F").length,
    I: items.filter((item) => getType(item) === "I").length,
  };

  const visibleTypeCounts = {
    A: visibleItems.filter((item) => getType(item) === "A").length,
    F: visibleItems.filter((item) => getType(item) === "F").length,
    I: visibleItems.filter((item) => getType(item) === "I").length,
  };

  const handleLinkedModalOpen = (path) => {
    dispatch(pushModal(window.location.pathname));
    dispatch(hideModal());
    navigate(`/${orgId}/${path}`);
  };

  const getUserRoleLabel = (item, currentUserEmail) => {
    if (item.responsible === currentUserEmail)
      return t("kanban.roles.responsible");
    if (item.accountable?.includes(currentUserEmail))
      return t("kanban.roles.accountable");
    if (item.consulted?.includes(currentUserEmail))
      return t("kanban.roles.consulted");
    if (item.informed?.includes(currentUserEmail))
      return t("kanban.roles.informed");
    if (item.reporter === currentUserEmail)
      return t("kanban.roles.reporter");
    return "";
  };

  const getStatusLabel = (status) => {
    if (!status) return "";

    const key = status.replace(/\s+/g, "").toLowerCase();
    return t(`kanban.statuses.${key}`, status);
  };

  return (
    <div className={styles.column}>
      <div className={styles.header}>
        {getStatusLabel(status).toUpperCase()}{" "}
        <span className={styles.count}>
          ({items.length > 99 ? "99+" : items.length})
        </span>
      </div>
      <div className={styles.container}>
        {visibleItems.map((item) => (
          <div
            className={styles.card}
            key={item.key}
            onClick={() => {
              const prefix = getType(item);
              const pathMap = {
                A: "audits",
                F: "findings",
                I: "improvement-projects",
              };
              const path = `${pathMap[prefix]}/${item.key}`;
              handleLinkedModalOpen(path);
            }}
          >
            <div className={styles.titleRow}>
              <span className={`${styles.typeBadge} ${styles[getType(item)]}`}>
                <img
                  src={
                    getType(item) === "A"
                      ? LargeAuditIcon
                      : getType(item) === "F"
                        ? LargeFindingIcon
                        : getType(item) === "I"
                          ? LargeImprovementIcon
                          : undefined
                  }
                  alt={t(
                    iconAltKeyMap[getType(item)] || "kanban.icons.audit",
                  )}
                  className={styles.cardIcon}
                />
              </span>
              <span className={styles.titleText}>
                {item.name || item.reference || t("kanban.untitled")}
              </span>
            </div>
            <div className={styles.meta}>
              <div className={styles.date}>
                {item.date && (
                  <div
                    className={
                      new Date(item.date) < new Date() && status !== "Done"
                        ? styles.overdue
                        : styles.date
                    }
                  >
                    <CalendarIcon
                      className={classNames(
                        styles.calendarIcon,
                        new Date(item.date) < new Date() &&
                        status !== "Done" &&
                        styles.overdue,
                      )}
                    />
                    <span className={styles.dateLabel}>
                      {item.key?.startsWith("A-")
                        ? t("kanban.dueDateLabel")
                        : t("kanban.reportedAtLabel")}
                    </span>
                    <span>{formatShortDate(item.date)}</span>
                    {new Date(item.date) < new Date() && status !== "Done" && (
                      <WarningIcon className={styles.warningIcon} />
                    )}
                  </div>
                )}
              </div>
              <span className={styles.userRole}>
                <UserIcon className={styles.userIcon} />
                {getUserRoleLabel(item, userEmail)}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className={styles.footer}>
        {["A", "F", "I"]
          .filter(
            (type) =>
              activeFilters.length === 0 || activeFilters.includes(type),
          )
          .map((type, index, filteredArray) => {
            const hidden = Math.max(
              typeCounts[type] - visibleTypeCounts[type],
              0,
            );

            const iconMap = {
              A: SmallAuditIcon,
              F: SmallFindingIcon,
              I: SmallImprovementIcon,
            };

            return (
              <React.Fragment key={type}>
                <span
                  key={type}
                  className={styles.footerLink}
                  onClick={() =>
                    navigate(
                      `${typeMap[type]}?status=${encodeURIComponent(status)}`,
                      {
                        state: { fromKanban: true },
                      },
                    )
                  }
                >
                  <span className={`${styles.typeBadge} ${styles[type]}`}>
                    <img
                      src={iconMap[type]}
                      alt={t(iconAltKeyMap[type] || "kanban.icons.audit")}
                      className={styles.footerIcon}
                    />
                  </span>
                  <span className={styles.moreText}>
                    {hidden > 99 ? "+99" : t("kanban.moreItems", { count: hidden })}
                  </span>
                  <LinkIcon className={styles.linkIcon} />
                </span>
                {index < filteredArray.length - 1 && (
                  <div className={styles.separator} />
                )}
              </React.Fragment>
            );
          })}
      </div>
    </div>
  );
};

export default KanbanColumn;
